import React, { useState } from 'react';
import { Plus, Search, AlertTriangle } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useAppStore } from '../contexts/AppStore';

export const IpPage: React.FC = () => {
  const { ipAssets } = useAppStore();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtrar activos de PI
  const filteredIpAssets = ipAssets.filter(ip =>
    ip.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ip.type.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Verificar si una fecha crítica está próxima (menos de 90 días)
  const isCriticalDateNear = (date: string) => {
    const daysUntil = differenceInDays(new Date(date), new Date());
    return daysUntil <= 90 && daysUntil >= 0;
  };
  
  const getStatusVariant = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'danger'> = {
      'Concedida': 'success',
      'Presentada': 'warning',
      'Abandonada': 'danger',
    };
    return variants[status] || 'default';
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Propiedad Intelectual</h1>
          <p className="text-gray-600 mt-1">
            Gestión del portafolio de activos de propiedad intelectual
          </p>
        </div>
        
        <Button
          variant="primary"
          className="flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Registrar Nuevo Activo</span>
        </Button>
      </div>
      
      {/* Barra de búsqueda */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por título o tipo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          <option value="">Todos los tipos</option>
          <option value="Patente">Patente</option>
          <option value="Software">Software</option>
          <option value="Secreto">Secreto Industrial</option>
        </select>
      </div>
      
      {/* Tabla de activos de PI */}
      <Card padding={false}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                  Título
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                  Tipo
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                  Estado
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                  Fecha Crítica
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                  Proyecto Vinculado
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredIpAssets.map((ip) => {
                const isNearCriticalDate = isCriticalDateNear(ip.criticalDate);
                const daysUntil = differenceInDays(new Date(ip.criticalDate), new Date());
                
                return (
                  <tr
                    key={ip.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      isNearCriticalDate ? 'bg-amber-50' : ''
                    }`}
                  >
                    <td className="py-4 px-6">
                      <p className="text-sm font-medium text-gray-900">{ip.title}</p>
                      {ip.description && (
                        <p className="text-xs text-gray-600 mt-1">{ip.description}</p>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {ip.type}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <Badge variant={getStatusVariant(ip.status)}>
                        {ip.status}
                      </Badge>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        {isNearCriticalDate && (
                          <AlertTriangle className="text-amber-600" size={18} />
                        )}
                        <div>
                          <p className={`text-sm font-medium ${
                            isNearCriticalDate ? 'text-amber-900' : 'text-gray-900'
                          }`}>
                            {format(new Date(ip.criticalDate), 'dd MMM yyyy', { locale: es })}
                          </p>
                          {isNearCriticalDate && (
                            <p className="text-xs text-amber-700">
                              {daysUntil} días restantes
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-sm text-gray-600">{ip.linkedProjectId}</p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      
      {/* Alertas de fechas críticas */}
      {ipAssets.some(ip => isCriticalDateNear(ip.criticalDate)) && (
        <Card className="border-l-4 border-amber-500 bg-amber-50">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="text-amber-600 mt-0.5" size={24} />
            <div>
              <h3 className="text-lg font-semibold text-amber-900">
                Fechas Críticas Próximas
              </h3>
              <p className="text-sm text-amber-800 mt-1">
                Hay activos de PI con fechas críticas en los próximos 90 días. 
                Revisa las filas resaltadas para tomar acción.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
